import { useState } from "react";
import { Heart, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { isIOSApp } from "@/lib/platform";
import WebsiteOnlyNotice from "@/components/home/WebsiteOnlyNotice";

export const MIN_DONATION = 1.99;
const QUICK = [1.99, 5, 10, 25, 50];
const PAY_EMAIL = "simplicityfilmsllc@gmail.com";
const METHODS = ["Visa", "Mastercard", "Cash App", "PayPal", "Venmo", "Zelle"];

function ManualOption({ label, value, hint }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 bg-black/40 border border-white/10 px-4 py-3" data-testid={`manual-pay-${label.toLowerCase()}`}>
      <div className="min-w-0">
        <p className="font-display font-bold uppercase text-sm">{label}</p>
        <p className="font-mono text-[11px] text-gray-400 truncate">{hint} <span className="text-gray-200">{value}</span></p>
      </div>
      <button type="button" onClick={copy} className="shrink-0 text-gray-400 hover:text-blood transition-colors" aria-label={`Copy ${label} details`}>
        {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
      </button>
    </div>
  );
}

export default function DonateForm() {
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const value = custom !== "" ? parseFloat(custom) : amount;
  const valid = !Number.isNaN(value) && value >= MIN_DONATION;

  if (isIOSApp()) {
    return (
      <div className="w-full h-full overflow-y-auto bg-ink p-6 sm:p-10 flex flex-col justify-center max-w-xl" data-testid="donate-panel">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-3">Fund the movement</p>
        <h3 className="font-display font-black uppercase text-3xl sm:text-4xl leading-[0.95] mb-6">Every dollar keeps<br />the signal alive</h3>
        <WebsiteOnlyNotice what="Donations" path="/?open=fundraising" testId="ios-donate-notice" />
      </div>
    );
  }

  const donate = async (e) => {
    e.preventDefault();
    if (!valid) return toast.error(`Minimum donation is $${MIN_DONATION.toFixed(2)}`);
    setBusy(true);
    try {
      const { data } = await api.post("/payments/donate", {
        amount: Math.round(value * 100) / 100, ...form, email: form.email || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto bg-ink p-6 sm:p-10 grid lg:grid-cols-[1.2fr_1fr] gap-8" data-testid="donate-panel">
      <form onSubmit={donate} className="space-y-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-3">Fund the movement</p>
          <h3 className="font-display font-black uppercase text-3xl sm:text-4xl leading-[0.95]">
            Every dollar keeps<br />the signal alive
          </h3>
          <p className="mt-3 text-sm text-gray-400">Minimum donation ${MIN_DONATION.toFixed(2)}. Secure checkout by Stripe.</p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="donate-quick-picks">
          {QUICK.map((q) => (
            <button key={q} type="button" data-testid={`donate-pick-${q}`}
              onClick={() => { setAmount(q); setCustom(""); }}
              className={`py-3 font-display font-bold text-lg border transition-colors ${custom === "" && amount === q ? "bg-blood border-blood text-white" : "border-white/15 text-gray-300 hover:border-blood"}`}>
              ${q}
            </button>
          ))}
        </div>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display font-bold text-gray-400">$</span>
          <input data-testid="donate-custom-amount" type="number" min={MIN_DONATION} step="0.01" value={custom}
            onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount"
            className="w-full bg-black/40 border border-white/15 pl-8 pr-4 py-3 font-mono text-sm focus:outline-none focus:border-blood" />
        </div>
        {custom !== "" && !valid && (
          <p data-testid="donate-min-error" className="font-mono text-xs text-blood">Minimum donation is ${MIN_DONATION.toFixed(2)}</p>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="donate-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name (optional)"
            className="bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-blood" />
          <input data-testid="donate-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email for receipt (optional)"
            className="bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-blood" />
        </div>
        <textarea data-testid="donate-message" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Leave a message for Ali Zo & Jean Luc (optional)"
          className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-blood resize-none" />

        <button data-testid="donate-submit-button" type="submit" disabled={busy || !valid}
          className="w-full flex items-center justify-center gap-3 bg-blood hover:bg-red-700 disabled:opacity-50 text-white font-display font-bold uppercase tracking-wider text-lg py-4 transition-colors">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Heart size={18} fill="currentColor" />}
          Donate {valid ? `$${value.toFixed(2)}` : ""} — Card / Cash App
        </button>
        <div className="flex flex-wrap gap-2" data-testid="accepted-methods">
          {METHODS.map((m) => (
            <span key={m} className="font-mono text-[10px] uppercase tracking-widest border border-white/15 px-2 py-1 text-gray-400">{m}</span>
          ))}
        </div>
      </form>

      <div className="space-y-3">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gray-400 mb-4">Other ways to give</p>
        <ManualOption label="PayPal" value={PAY_EMAIL} hint="Send to" />
        <ManualOption label="Zelle" value={PAY_EMAIL} hint="Send to" />
        <ManualOption label="Venmo" value={PAY_EMAIL} hint="Search by email" />
        <ManualOption label="Cash App" value="Pay via the Donate button" hint="" />
        <p className="font-mono text-[11px] text-gray-500 leading-relaxed pt-2">
          Put “Zo Pound” in the note so we can thank you. Minimum ${MIN_DONATION.toFixed(2)} on every method.
        </p>
      </div>
    </div>
  );
}
